import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailOutlined, UndoOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import EquipmentToReturn from '@/components/equipment-requests/EquipmentToReturn';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import TerminationNotifyDialog from './TerminationNotifyDialog';
import { terminationQueryKey } from './terminationShared';

/**
 * The termination in effect, under the employment line of the "Contract"
 * tile: last working day, reason, its documents, whether anyone was notified,
 * and the "Notify" and "Undo termination" actions. Undo is the same as
 * deleting the termination's document from the documents list (which still
 * works); a termination entered without a document can only be undone here.
 */
export default function TerminationSummary({ employeeId }) {
  const { t } = useTranslation(['translations']);
  const tt = (key, opts) =>
    t(`terminationUi.${key}`, { ns: 'translations', ...opts });
  const notification = useNotify();
  const queryClient = useQueryClient();
  const [notifyOpen, setNotifyOpen] = useState(false);

  // A role without the termination permission is refused: no summary then.
  const { data } = useQuery({
    queryKey: terminationQueryKey(employeeId),
    queryFn: () => API.employees.getCurrentTermination(employeeId),
    enabled: Boolean(employeeId),
    retry: false,
  });
  const termination = data?.termination || null;

  const { mutate: undo, isPending: undoing } = useMutation({
    mutationFn: () =>
      API.employees.deleteTermination(employeeId, termination.contractId),
    onSuccess: (response) => {
      notification.success({ message: response?.message });
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      notification.error({
        message: error?.response?.data?.message || error?.message,
      });
    },
  });

  if (!termination) return null;

  const last = termination.notifications?.length
    ? termination.notifications[termination.notifications.length - 1]
    : null;
  const reason = termination.reason
    ? termination.reason === 'OTHER' && termination.reasonNote
      ? `${tt(`reasons.${termination.reason}`)}: ${termination.reasonNote}`
      : tt(`reasons.${termination.reason}`)
    : null;

  return (
    <div className="flex w-full flex-wrap items-center gap-2 border-t border-zinc-100 pt-2.5">
      <span className="min-w-0 flex-1 text-[11.5px] text-zinc-600">
        <span className="font-bold text-red-700">
          {tt('summaryTitle', {
            date: termination.endDate
              ? dayjs(termination.endDate).format(DATE_FORMATS.DISPLAY)
              : '—',
          })}
        </span>
        {reason && <span> · {reason}</span>}
        <span>
          {' '}
          ·{' '}
          {termination.documents?.length
            ? termination.documents.map((d) => d.title).join(', ')
            : tt('noDocument')}
        </span>
        <span
          className={
            last ? 'block text-zinc-500' : 'block font-semibold text-amber-700'
          }
        >
          {last
            ? tt('lastNotified', {
                date: dayjs(last.sentAt).format(DATE_FORMATS.DISPLAY),
                n: last.recipients?.length || 0,
              })
            : tt('notNotifiedYet')}
        </span>
        <EquipmentToReturn employeeId={employeeId} className="mt-0.5" />
      </span>
      <span className="flex gap-1.5">
        <Button
          icon={<MailOutlined />}
          className="btn-default btn-default--hover !h-7 !rounded-[50px] !px-2.5 !text-xs"
          onClick={() => setNotifyOpen(true)}
        >
          {last ? tt('notifyAgain') : tt('notify')}
        </Button>
        <Popconfirm
          title={tt('undoConfirm')}
          okButtonProps={{ className: '!rounded-[50px]' }}
          cancelButtonProps={{ className: '!rounded-[50px]' }}
          okText={tt('undo')}
          cancelText={tt('cancel')}
          onConfirm={() => undo()}
        >
          <Button
            icon={<UndoOutlined />}
            loading={undoing}
            className="btn-default btn-default--hover !h-7 !rounded-[50px] !px-2.5 !text-xs"
          >
            {tt('undo')}
          </Button>
        </Popconfirm>
      </span>
      <TerminationNotifyDialog
        employeeId={employeeId}
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
      />
    </div>
  );
}
